import { useState, useMemo, useEffect } from 'react';
import { useI18n } from '../lib/i18n';
import {
  useSubscribers,
  Subscriber,
  SubscriberFilter,
  matchesSubscriberFilter,
  calculateSubscriberStats,
  subscriberBulk,
  subscriberExport,
  deleteSubscriber,
  type OptInStatus,
} from '../lib/subscribers';
import { useLabels, useLists, useCustomFields } from '../lib/crm';
import { useAccounts, ALL_ACCOUNTS } from '../lib/accounts';
import { numberLabel } from '../lib/whatsappApi';
import { useSequences } from '../lib/sequences';

import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';
import { useConfirm } from '../components/ui/ConfirmDialog';

import AddSubscriberModal from '../components/subscribers/AddSubscriberModal';
import ImportSubscribersModal from '../components/subscribers/ImportSubscribersModal';
import LabelsManagerModal from '../components/subscribers/LabelsManagerModal';
import ListsManagerModal from '../components/subscribers/ListsManagerModal';
import CustomFieldsManagerModal from '../components/subscribers/CustomFieldsManagerModal';
import SubscriberDrawer from '../components/subscribers/SubscriberDrawer';
import ConsentNoteDialog from '../components/subscribers/ConsentNoteDialog';

import {
  SearchIcon,
  FilterIcon,
  DownloadIcon,
  UploadIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckIcon,
  TrashIcon,
  SubscribersIcon,
  HelpCircleIcon,
} from '../components/icons';

export default function Subscribers() {
  const { t } = useI18n();
  const confirm = useConfirm();
  const accounts = useAccounts();
  const { subscribers, loading, error: subError } = useSubscribers();
  const { labels } = useLabels();
  const { lists } = useLists();
  const { customFields } = useCustomFields();
  const { sequences } = useSequences();

  // ── Filters State ───────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [labelFilter, setLabelFilter] = useState<string>('all');
  const [listFilter, setListFilter] = useState<string>('all');
  const [sequenceFilter, setSequenceFilter] = useState<string>('all');
  const [accountFilter, setAccountFilter] = useState<string>(accounts.selectedId || ALL_ACCOUNTS);

  // ── Pagination State ────────────────────────────────────────────────────────
  const [pageSize, setPageSize] = useState<number>(25);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // ── Selection State ─────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState<boolean>(false);

  // ── Modals & Drawer State ───────────────────────────────────────────────────
  const [activeSubscriber, setActiveSubscriber] = useState<Subscriber | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isLabelsModalOpen, setIsLabelsModalOpen] = useState(false);
  const [isListsModalOpen, setIsListsModalOpen] = useState(false);
  const [isCustomFieldsModalOpen, setIsCustomFieldsModalOpen] = useState(false);
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const [consentMode, setConsentMode] = useState<'resubscribe' | 'marketing'>('resubscribe');

  // ── Dropdown Menus State ────────────────────────────────────────────────────
  const [showExportImportMenu, setShowExportImportMenu] = useState(false);
  const [showManageMenu, setShowManageMenu] = useState(false);
  const [showBulkActionsMenu, setShowBulkActionsMenu] = useState(false);
  const [bulkActionSubmenu, setBulkActionSubmenu] = useState<
    'addLabel' | 'removeLabel' | 'addList' | 'removeList' | 'assignSequence' | null
  >(null);

  // ── Busy / Feedback State ───────────────────────────────────────────────────
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(
    null,
  );

  // Sync account filter with accounts context
  useEffect(() => {
    if (accounts.selectedId) {
      setAccountFilter(accounts.selectedId);
    }
  }, [accounts.selectedId]);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setFeedback({ type, message });
    setTimeout(() => setFeedback(null), 4000);
  };

  // Build active filter payload
  const activeFilter = useMemo<SubscriberFilter>(() => {
    const f: SubscriberFilter = {};
    if (searchQuery.trim()) f.search = searchQuery.trim();
    if (statusFilter !== 'all') f.status = statusFilter as OptInStatus;
    if (labelFilter !== 'all') f.labelIds = [labelFilter];
    if (listFilter !== 'all') f.listIds = [listFilter];
    if (accountFilter !== ALL_ACCOUNTS && accountFilter) f.integrationIds = [accountFilter];
    return f;
  }, [searchQuery, statusFilter, labelFilter, listFilter, accountFilter]);

  // Filter subscribers in memory
  const filteredSubscribers = useMemo(() => {
    return subscribers.filter((sub) => {
      // Basic filter check
      if (!matchesSubscriberFilter(sub, activeFilter)) return false;
      // Sequence filter check
      if (sequenceFilter !== 'all') {
        if (!sub.sequenceIds || !sub.sequenceIds.includes(sequenceFilter)) return false;
      }
      return true;
    });
  }, [subscribers, activeFilter, sequenceFilter]);

  // Overall Statistics
  const stats = useMemo(() => calculateSubscriberStats(subscribers), [subscribers]);

  // Pagination calculation
  const totalItems = filteredSubscribers.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
  const pagedSubscribers = useMemo(() => {
    const start = (safeCurrentPage - 1) * pageSize;
    return filteredSubscribers.slice(start, start + pageSize);
  }, [filteredSubscribers, safeCurrentPage, pageSize]);

  // Selection helpers
  const allPageSelected =
    pagedSubscribers.length > 0 && pagedSubscribers.every((s) => selectedIds.has(s.id));

  const handleToggleRow = (id: string) => {
    if (selectAllMatching) setSelectAllMatching(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleSelectPage = () => {
    if (selectAllMatching) {
      setSelectAllMatching(false);
      setSelectedIds(new Set());
      return;
    }
    if (allPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pagedSubscribers.forEach((s) => next.delete(s.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pagedSubscribers.forEach((s) => next.add(s.id));
        return next;
      });
    }
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
    setSelectAllMatching(false);
  };

  const handleSelectAllMatching = () => {
    setSelectAllMatching(true);
    // Also include currently paged IDs for fast visual feedback
    const all = new Set(filteredSubscribers.map((s) => s.id));
    setSelectedIds(all);
  };

  // Has active filters
  const hasActiveFilters =
    Boolean(searchQuery.trim()) ||
    statusFilter !== 'all' ||
    labelFilter !== 'all' ||
    listFilter !== 'all' ||
    sequenceFilter !== 'all' ||
    accountFilter !== ALL_ACCOUNTS;

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setLabelFilter('all');
    setListFilter('all');
    setSequenceFilter('all');
    setAccountFilter(ALL_ACCOUNTS);
    setCurrentPage(1);
  };

  // ── Bulk Actions Execution ──────────────────────────────────────────────────
  const executeBulkAction = async (
    action:
      | 'addLabel'
      | 'removeLabel'
      | 'addList'
      | 'removeList'
      | 'assignSequence'
      | 'delete'
      | 'unsubscribe'
      | 'resubscribe'
      | 'recordMarketingConsent'
      | 'resetInputFlow'
      | 'clearHistory',
    opts?: {
      labelId?: string;
      listId?: string;
      sequenceId?: string;
      consentNote?: string;
    },
  ) => {
    setShowBulkActionsMenu(false);
    setBulkActionSubmenu(null);
    setBusy(true);

    try {
      const payload: Parameters<typeof subscriberBulk>[0] = {
        action,
        ...(opts?.labelId ? { labelId: opts.labelId } : {}),
        ...(opts?.listId ? { listId: opts.listId } : {}),
        ...(opts?.sequenceId ? { sequenceId: opts.sequenceId } : {}),
        ...(opts?.consentNote ? { consentNote: opts.consentNote } : {}),
      };

      if (selectAllMatching) {
        payload.filter = activeFilter;
      } else {
        payload.ids = Array.from(selectedIds);
      }

      const res = await subscriberBulk(payload);
      if (action === 'recordMarketingConsent') {
        showNotification(
          'success',
          t('subs.mconsent.done', { count: res.affectedCount, skipped: res.skippedOptedOut ?? 0 }),
        );
      } else {
        showNotification(
          'success',
          `Bulk operation completed. Affected ${res.affectedCount} contacts.`,
        );
      }
      handleClearSelection();
    } catch (e) {
      showNotification('error', (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Resubscribe confirmation from ConsentNoteDialog
  const handleBulkConsentConfirm = async (consentNote: string) => {
    setIsConsentModalOpen(false);
    await executeBulkAction(
      consentMode === 'marketing' ? 'recordMarketingConsent' : 'resubscribe',
      { consentNote },
    );
  };

  // Export subscribers to CSV
  const handleExportCsv = async () => {
    setShowExportImportMenu(false);
    setBusy(true);
    try {
      const res = await subscriberExport({
        filter: selectAllMatching
          ? activeFilter
          : selectedIds.size > 0
          ? { search: searchQuery || undefined }
          : activeFilter,
        format: 'csv',
      });

      if (res.downloadUrl) {
        const link = document.createElement('a');
        link.href = res.downloadUrl;
        link.setAttribute('download', `subscribers_export_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showNotification('success', t('subs.exportSuccess', { n: res.totalRows }) || `Exported ${res.totalRows} contacts to CSV.`);
      }
    } catch (e) {
      showNotification('error', (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Delete single subscriber
  const handleDeleteSubscriber = async (subscriberId: string) => {
    const ok = await confirm({
      title: t('subs.deleteConfirmTitle') || 'Delete subscriber?',
      body: t('subs.deleteConfirmBody') || 'Are you sure you want to delete this subscriber?',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await deleteSubscriber({ subscriberId });
      showNotification('success', t('subs.deleteSuccess') || 'Subscriber removed successfully.');
      if (activeSubscriber?.id === subscriberId) {
        setActiveSubscriber(null);
      }
    } catch (e) {
      showNotification('error', (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Lookup Maps
  const labelMap = useMemo(() => new Map(labels.map((l) => [l.id, l])), [labels]);
  const listMap = useMemo(() => new Map(lists.map((l) => [l.id, l])), [lists]);

  const effectiveSelectedCount = selectAllMatching ? totalItems : selectedIds.size;

  return (
    <div className="space-y-6">
      {/* 1. Page Header & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <PageHeader
          title={t('subs.title') || 'Subscribers'}
          subtitle={
            t('subs.subtitle') ||
            'Manage customer contacts, audience lists, labels, and WhatsApp opt-in compliance.'
          }
          className="mb-0"
        />

        <div className="flex items-center gap-2.5 shrink-0">
          {/* Export / Import Dropdown */}
          <div className="relative">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setShowExportImportMenu(!showExportImportMenu);
                setShowManageMenu(false);
              }}
              className="flex items-center gap-1.5"
            >
              <span>{t('subs.actions.exportImport') || 'Export / Import'}</span>
              <ChevronDownIcon size={14} />
            </Button>

            {showExportImportMenu && (
              <div className="absolute end-0 mt-1 w-56 rounded-xl border border-slate-200 bg-white py-1 shadow-lg z-20 text-xs">
                <button
                  type="button"
                  onClick={() => {
                    setShowExportImportMenu(false);
                    setIsAddModalOpen(true);
                  }}
                  className="w-full text-start px-4 py-2 hover:bg-slate-50 flex items-center gap-2 text-slate-700"
                >
                  <PlusIcon size={14} className="text-indigo-600" />
                  <span>{t('subs.add.btn') || 'Add Single Contact'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowExportImportMenu(false);
                    setIsImportModalOpen(true);
                  }}
                  className="w-full text-start px-4 py-2 hover:bg-slate-50 flex items-center gap-2 text-slate-700"
                >
                  <UploadIcon size={14} className="text-emerald-600" />
                  <span>{t('subs.import.btn') || 'Import Subscribers (CSV)'}</span>
                </button>
                <div className="my-1 border-t border-slate-100" />
                <button
                  type="button"
                  onClick={handleExportCsv}
                  disabled={busy}
                  className="w-full text-start px-4 py-2 hover:bg-slate-50 flex items-center gap-2 text-slate-700"
                >
                  <DownloadIcon size={14} className="text-blue-600" />
                  <span>{t('subs.export.btn') || 'Export Filtered to CSV'}</span>
                </button>
              </div>
            )}
          </div>

          {/* Manage Dropdown (Labels, Lists, Custom Fields) */}
          <div className="relative">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setShowManageMenu(!showManageMenu);
                setShowExportImportMenu(false);
              }}
              className="flex items-center gap-1.5"
            >
              <FilterIcon size={14} />
              <span>{t('subs.actions.manage') || 'Manage'}</span>
              <ChevronDownIcon size={14} />
            </Button>

            {showManageMenu && (
              <div className="absolute end-0 mt-1 w-52 rounded-xl border border-slate-200 bg-white py-1 shadow-lg z-20 text-xs">
                <button
                  type="button"
                  onClick={() => {
                    setShowManageMenu(false);
                    setIsLabelsModalOpen(true);
                  }}
                  className="w-full text-start px-4 py-2 hover:bg-slate-50 text-slate-700 flex items-center justify-between"
                >
                  <span>{t('subs.manage.labels') || 'Labels Manager'}</span>
                  <span className="text-[11px] text-slate-400 font-mono font-medium">
                    {labels.length}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowManageMenu(false);
                    setIsListsModalOpen(true);
                  }}
                  className="w-full text-start px-4 py-2 hover:bg-slate-50 text-slate-700 flex items-center justify-between"
                >
                  <span>{t('subs.manage.lists') || 'Subscriber Lists'}</span>
                  <span className="text-[11px] text-slate-400 font-mono font-medium">
                    {lists.length}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowManageMenu(false);
                    setIsCustomFieldsModalOpen(true);
                  }}
                  className="w-full text-start px-4 py-2 hover:bg-slate-50 text-slate-700 flex items-center justify-between"
                >
                  <span>{t('subs.manage.customFields') || 'Custom Attributes'}</span>
                  <span className="text-[11px] text-slate-400 font-mono font-medium">
                    {customFields.length}
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Quick Primary Button */}
          <Button
            variant="primary"
            size="sm"
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-1.5"
          >
            <PlusIcon size={16} />
            <span>{t('subs.add.btn') || 'Add Subscriber'}</span>
          </Button>
        </div>
      </div>

      {/* Feedback Alert */}
      {feedback && (
        <div
          className={`p-3.5 rounded-xl border text-xs flex items-center justify-between ${
            feedback.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-rose-50 border-rose-200 text-rose-800'
          }`}
        >
          <div className="flex items-center gap-2">
            {feedback.type === 'success' ? (
              <CheckIcon size={16} className="text-emerald-600 shrink-0" />
            ) : (
              <HelpCircleIcon size={16} className="text-rose-600 shrink-0" />
            )}
            <span>{feedback.message}</span>
          </div>
          <button
            type="button"
            onClick={() => setFeedback(null)}
            className="text-slate-400 hover:text-slate-600 text-sm px-1 font-bold"
          >
            ×
          </button>
        </div>
      )}

      {/* 2. Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {loading ? (
          <>
            <Skeleton.StatCard />
            <Skeleton.StatCard />
            <Skeleton.StatCard />
          </>
        ) : (
          <>
            <StatCard
              label={t('subs.stats.total') || 'Total Subscribers'}
              value={stats.total.toLocaleString()}
              caption={t('subs.stats.allChannels') || 'WhatsApp audience'}
              icon={<SubscribersIcon size={20} className="text-indigo-600" />}
              variant="indigo"
            />
            <StatCard
              label={t('subs.stats.subscribed') || 'Subscribed'}
              value={`${stats.subscribedPct}%`}
              caption={`${subscribers.filter((s) => s.status === 'subscribed').length} active opt-ins`}
              icon={<CheckIcon size={20} className="text-emerald-600" />}
              variant="green"
            />
            <StatCard
              label={t('subs.stats.inSequences') || 'In Sequences'}
              value={`${stats.inSequencesPct}%`}
              caption={`${
                subscribers.filter((s) => s.sequenceIds && s.sequenceIds.length > 0).length
              } enrolled in drips`}
              icon={<FilterIcon size={20} className="text-blue-600" />}
              variant="sky"
            />
          </>
        )}
      </div>

      {/* 3. Filter Bar */}
      <div className="p-4 bg-white rounded-xl border border-slate-200 space-y-3 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
          {/* Search Box */}
          <div className="lg:col-span-2 relative">
            <SearchIcon
              size={15}
              className="absolute start-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              placeholder={t('subs.filter.searchPlaceholder') || 'Search name, phone, or email...'}
              className="w-full rounded-lg border border-slate-200 bg-white ps-8 pe-3 py-1.5 text-xs outline-none focus:border-indigo-500"
            />
          </div>

          {/* WhatsApp Bot / Line Account Filter */}
          {accounts.numbers.length > 1 && (
            <div>
              <select
                value={accountFilter}
                onChange={(e) => {
                  setAccountFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-indigo-500"
              >
                <option value={ALL_ACCOUNTS}>{t('subs.filter.allAccounts') || 'All Bot Lines'}</option>
                {accounts.numbers.map((n) => (
                  <option key={n.id} value={n.id}>
                    {numberLabel(n)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-indigo-500"
            >
              <option value="all">{t('subs.filter.allStatuses') || 'All Opt-In Status'}</option>
              <option value="subscribed">{t('subs.status.subscribed') || 'Subscribed'}</option>
              <option value="unsubscribed">{t('subs.status.unsubscribed') || 'Opted Out'}</option>
              <option value="pending">{t('subs.status.pending') || 'Pending'}</option>
            </select>
          </div>

          {/* Label Filter */}
          <div>
            <select
              value={labelFilter}
              onChange={(e) => {
                setLabelFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-indigo-500"
            >
              <option value="all">{t('subs.filter.allLabels') || 'All Labels'}</option>
              {labels.map((l) => (
                <option key={l.id} value={l.id}>
                  🏷️ {l.name}
                </option>
              ))}
            </select>
          </div>

          {/* List Filter */}
          <div>
            <select
              value={listFilter}
              onChange={(e) => {
                setListFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-indigo-500"
            >
              <option value="all">{t('subs.filter.allLists') || 'All Lists'}</option>
              {lists.map((lst) => (
                <option key={lst.id} value={lst.id}>
                  📋 {lst.name}
                </option>
              ))}
            </select>
          </div>

          {/* Sequence Filter */}
          <div>
            <select
              value={sequenceFilter}
              onChange={(e) => {
                setSequenceFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-indigo-500"
            >
              <option value="all">{t('subs.filter.allSequences') || 'All Sequences'}</option>
              {sequences.map((s) => (
                <option key={s.id} value={s.id}>
                  ⚡ {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Clear Filters Action */}
        {hasActiveFilters && (
          <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-100">
            <span className="text-slate-500">
              {t('subs.filter.showingFiltered', { count: filteredSubscribers.length, total: subscribers.length }) || `Showing ${filteredSubscribers.length} of ${subscribers.length} subscribers matching filters`}
            </span>
            <button
              type="button"
              onClick={handleClearFilters}
              className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
            >
              {t('subs.filter.clearAll') || 'Clear All Filters'}
            </button>
          </div>
        )}
      </div>

      {/* 4. Selection Banner & Bulk Actions Toolbar */}
      {effectiveSelectedCount > 0 && (
        <div className="p-3 bg-indigo-50/80 border border-indigo-200 rounded-xl flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-indigo-950">
              {selectAllMatching ? (
                <>{t('subs.bulk.allMatchingSelected', { count: totalItems }) || `All ${totalItems} subscribers matching current filter are selected.`}</>
              ) : (
                <>{t('subs.bulk.selectedOnView', { count: selectedIds.size }) || `Selected ${selectedIds.size} subscriber(s) on this view.`}</>
              )}
            </span>

            {!selectAllMatching && totalItems > selectedIds.size && (
              <button
                type="button"
                onClick={handleSelectAllMatching}
                className="font-bold text-indigo-600 hover:text-indigo-800 underline ms-2"
              >
                {t('subs.bulk.selectAllMatching', { count: totalItems }) || `Select all ${totalItems} matching contacts`}
              </button>
            )}

            <button
              type="button"
              onClick={handleClearSelection}
              className="text-slate-500 hover:text-slate-700 underline ms-2"
            >
              {t('subs.bulk.clearSelection') || 'Clear selection'}
            </button>
          </div>

          {/* Bulk Actions Menu Button */}
          <div className="relative">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowBulkActionsMenu(!showBulkActionsMenu)}
              disabled={busy}
              className="flex items-center gap-1.5"
            >
              <span>{t('subs.bulk.actions') || 'Bulk Actions'}</span>
              <ChevronDownIcon size={14} />
            </Button>

            {showBulkActionsMenu && (
              <div className="absolute end-0 mt-1 w-56 rounded-xl border border-slate-200 bg-white py-1 shadow-xl z-30 text-xs">
                {/* Add Label */}
                <button
                  type="button"
                  onClick={() =>
                    setBulkActionSubmenu(bulkActionSubmenu === 'addLabel' ? null : 'addLabel')
                  }
                  className="w-full text-start px-4 py-2 hover:bg-slate-50 flex items-center justify-between text-slate-700"
                >
                  <span>{t('subs.bulk.addLabel') || 'Add Label'}</span>
                  <ChevronDownIcon size={14} className="text-slate-400 -rotate-90 rtl:rotate-90" />
                </button>

                {bulkActionSubmenu === 'addLabel' && (
                  <div className="bg-slate-50 p-2 space-y-1 max-h-36 overflow-y-auto border-y border-slate-100">
                    {labels.map((l) => (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => executeBulkAction('addLabel', { labelId: l.id })}
                        className="w-full text-start px-2 py-1 hover:bg-white rounded text-[11px] flex items-center gap-1.5"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: l.color }}
                        />
                        <span>{l.name}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Remove Label */}
                <button
                  type="button"
                  onClick={() =>
                    setBulkActionSubmenu(bulkActionSubmenu === 'removeLabel' ? null : 'removeLabel')
                  }
                  className="w-full text-start px-4 py-2 hover:bg-slate-50 flex items-center justify-between text-slate-700"
                >
                  <span>{t('subs.bulk.removeLabel') || 'Remove Label'}</span>
                  <ChevronDownIcon size={14} className="text-slate-400 -rotate-90 rtl:rotate-90" />
                </button>

                {bulkActionSubmenu === 'removeLabel' && (
                  <div className="bg-slate-50 p-2 space-y-1 max-h-36 overflow-y-auto border-y border-slate-100">
                    {labels.map((l) => (
                      <button
                        key={l.id}
                        type="button"
                        onClick={() => executeBulkAction('removeLabel', { labelId: l.id })}
                        className="w-full text-start px-2 py-1 hover:bg-white rounded text-[11px] flex items-center gap-1.5"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: l.color }}
                        />
                        <span>{l.name}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Add to List */}
                <button
                  type="button"
                  onClick={() =>
                    setBulkActionSubmenu(bulkActionSubmenu === 'addList' ? null : 'addList')
                  }
                  className="w-full text-start px-4 py-2 hover:bg-slate-50 flex items-center justify-between text-slate-700"
                >
                  <span>{t('subs.bulk.addList') || 'Add to List'}</span>
                  <ChevronDownIcon size={14} className="text-slate-400 -rotate-90 rtl:rotate-90" />
                </button>

                {bulkActionSubmenu === 'addList' && (
                  <div className="bg-slate-50 p-2 space-y-1 max-h-36 overflow-y-auto border-y border-slate-100">
                    {lists.map((lst) => (
                      <button
                        key={lst.id}
                        type="button"
                        onClick={() => executeBulkAction('addList', { listId: lst.id })}
                        className="w-full text-start px-2 py-1 hover:bg-white rounded text-[11px]"
                      >
                        {lst.name}
                      </button>
                    ))}
                  </div>
                )}

                {/* Remove from List */}
                <button
                  type="button"
                  onClick={() =>
                    setBulkActionSubmenu(bulkActionSubmenu === 'removeList' ? null : 'removeList')
                  }
                  className="w-full text-start px-4 py-2 hover:bg-slate-50 flex items-center justify-between text-slate-700"
                >
                  <span>{t('subs.bulk.removeList') || 'Remove from List'}</span>
                  <ChevronDownIcon size={14} className="text-slate-400 -rotate-90 rtl:rotate-90" />
                </button>

                {bulkActionSubmenu === 'removeList' && (
                  <div className="bg-slate-50 p-2 space-y-1 max-h-36 overflow-y-auto border-y border-slate-100">
                    {lists.map((lst) => (
                      <button
                        key={lst.id}
                        type="button"
                        onClick={() => executeBulkAction('removeList', { listId: lst.id })}
                        className="w-full text-start px-2 py-1 hover:bg-white rounded text-[11px]"
                      >
                        {lst.name}
                      </button>
                    ))}
                  </div>
                )}

                {/* Enroll in Sequence */}
                <button
                  type="button"
                  onClick={() =>
                    setBulkActionSubmenu(
                      bulkActionSubmenu === 'assignSequence' ? null : 'assignSequence',
                    )
                  }
                  className="w-full text-start px-4 py-2 hover:bg-slate-50 flex items-center justify-between text-slate-700"
                >
                  <span>{t('subs.bulk.enrollSequence') || 'Enroll in Sequence'}</span>
                  <ChevronDownIcon size={14} className="text-slate-400 -rotate-90 rtl:rotate-90" />
                </button>

                {bulkActionSubmenu === 'assignSequence' && (
                  <div className="bg-slate-50 p-2 space-y-1 max-h-36 overflow-y-auto border-y border-slate-100">
                    {sequences.map((seq) => (
                      <button
                        key={seq.id}
                        type="button"
                        onClick={() => executeBulkAction('assignSequence', { sequenceId: seq.id })}
                        className="w-full text-start px-2 py-1 hover:bg-white rounded text-[11px]"
                      >
                        {seq.name}
                      </button>
                    ))}
                  </div>
                )}

                <div className="my-1 border-t border-slate-100" />

                {/* Unsubscribe */}
                <button
                  type="button"
                  onClick={() => executeBulkAction('unsubscribe')}
                  className="w-full text-start px-4 py-2 hover:bg-slate-50 text-slate-700"
                >
                  {t('subs.bulk.unsubscribe') || 'Unsubscribe'}
                </button>

                {/* Record marketing consent (needed for MARKETING templates) */}
                <button
                  type="button"
                  onClick={() => {
                    setShowBulkActionsMenu(false);
                    setConsentMode('marketing');
                    setIsConsentModalOpen(true);
                  }}
                  className="w-full text-start px-4 py-2 hover:bg-slate-50 text-indigo-700 font-semibold"
                >
                  {t('subs.bulk.recordMarketingConsent')}
                </button>

                {/* Resubscribe with Consent Dialog */}
                <button
                  type="button"
                  onClick={() => {
                    setShowBulkActionsMenu(false);
                    setConsentMode('resubscribe');
                    setIsConsentModalOpen(true);
                  }}
                  className="w-full text-start px-4 py-2 hover:bg-slate-50 text-emerald-700 font-semibold"
                >
                  {t('subs.bulk.resubscribe') || 'Resubscribe (Consent Required)'}
                </button>

                {/* Reset Flow */}
                <button
                  type="button"
                  onClick={() => executeBulkAction('resetInputFlow')}
                  className="w-full text-start px-4 py-2 hover:bg-slate-50 text-slate-700"
                >
                  {t('subs.bulk.resetFlow') || 'Reset Flow Position'}
                </button>

                {/* Clear History */}
                <button
                  type="button"
                  onClick={() => {
                    void (async () => {
                      const ok = await confirm({
                        title: t('subs.bulk.clearHistory') || 'Clear Chat History',
                        body: t('subs.bulk.clearHistoryConfirm') || 'Clear chat history for all selected contacts?',
                        danger: true,
                      });
                      if (ok) {
                        executeBulkAction('clearHistory');
                      }
                    })();
                  }}
                  className="w-full text-start px-4 py-2 hover:bg-slate-50 text-rose-600"
                >
                  {t('subs.bulk.clearHistory') || 'Clear Chat History'}
                </button>

                <div className="my-1 border-t border-slate-100" />

                {/* Delete Contacts */}
                <button
                  type="button"
                  onClick={() => {
                    void (async () => {
                      const ok = await confirm({
                        title: t('subs.bulk.deleteSelected') || 'Delete Selected',
                        body: t('subs.bulk.deleteConfirm', { count: effectiveSelectedCount }) || `Are you sure you want to delete ${effectiveSelectedCount} contacts?`,
                        danger: true,
                      });
                      if (ok) {
                        executeBulkAction('delete');
                      }
                    })();
                  }}
                  className="w-full text-start px-4 py-2 hover:bg-rose-50 text-rose-600 font-semibold"
                >
                  {t('subs.bulk.deleteSelected') || 'Delete Selected'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 5. Data Table / Card View */}
      {loading ? (
        <div className="space-y-4">
          {/* Desktop Table Skeleton */}
          <div className="hidden md:block bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-start text-xs border-collapse">
                <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-600">
                  <tr>
                    <th className="py-3 px-4 text-start w-10"></th>
                    <th className="py-3 px-4 text-start">{t('subs.col.contact') || 'Contact'}</th>
                    <th className="py-3 px-4 text-start">{t('subs.col.status') || 'Status'}</th>
                    <th className="py-3 px-4 text-start">{t('subs.col.labels') || 'Labels'}</th>
                    <th className="py-3 px-4 text-start">{t('subs.col.lists') || 'Lists'}</th>
                    <th className="py-3 px-4 text-start">{t('subs.col.lastActive') || 'Last Active'}</th>
                    <th className="py-3 px-4 text-end w-20">{t('subs.col.actions') || 'Actions'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton.TableRow key={i} cols={7} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* Mobile Cards Skeleton */}
          <div className="md:hidden space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton.Card key={i} className="p-4" />
            ))}
          </div>
        </div>
      ) : subError ? (
        <div className="p-8 text-center bg-red-50 text-red-700 rounded-xl border border-red-200 text-xs">
          {t('subs.errorLoading', { error: subError }) || `Error loading subscribers: ${subError}`}
        </div>
      ) : totalItems === 0 ? (
        <EmptyState
          title={hasActiveFilters ? (t('subs.noMatching') || 'No matching contacts') : (t('subs.empty.title') || 'No subscribers found')}
          description={
            hasActiveFilters
              ? (t('subs.empty.adjustFilters') || 'Try adjusting your search query or clearing some filters.')
              : (t('subs.empty.addOrImport') || 'Add contacts manually or import them using a CSV file.')
          }
          action={
            hasActiveFilters ? (
              <Button variant="secondary" onClick={handleClearFilters}>
                {t('subs.filter.clear') || 'Clear Filters'}
              </Button>
            ) : (
              <div className="flex items-center gap-3">
                <Button variant="primary" onClick={() => setIsAddModalOpen(true)} leadingIcon={<PlusIcon size={14} />}>
                  {t('subs.add.btn') || 'Add Subscriber'}
                </Button>
                <Button variant="secondary" onClick={() => setIsImportModalOpen(true)} leadingIcon={<UploadIcon size={14} />}>
                  {t('subs.import.title') || 'Import CSV'}
                </Button>
              </div>
            )
          }
        />
      ) : (
        <div className="space-y-4">
          {/* Desktop Table View */}
          <div className="hidden md:block bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-start text-xs border-collapse">
                <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-600">
                  <tr>
                    <th className="py-3 px-4 text-start w-10">
                      <input
                        type="checkbox"
                        checked={allPageSelected || selectAllMatching}
                        onChange={handleToggleSelectPage}
                        className="rounded border-slate-300 text-indigo-600 focus:ring-0"
                      />
                    </th>
                    <th className="py-3 px-4 text-start font-semibold">{t('subs.col.contactPhone') || 'Contact / Phone'}</th>
                    <th className="py-3 px-4 text-start font-semibold">{t('subs.col.optInStatus') || 'Opt-In Status'}</th>
                    <th className="py-3 px-4 text-start font-semibold">{t('subs.col.labels') || 'Labels'}</th>
                    <th className="py-3 px-4 text-start font-semibold">{t('subs.col.lists') || 'Lists'}</th>
                    <th className="py-3 px-4 text-start font-semibold">{t('subs.col.country') || 'Country'}</th>
                    <th className="py-3 px-4 text-start font-semibold">{t('subs.col.lastActive') || 'Last Active'}</th>
                    <th className="py-3 px-4 text-end font-semibold">{t('subs.col.actions') || 'Actions'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pagedSubscribers.map((sub) => {
                    const isSelected = selectedIds.has(sub.id) || selectAllMatching;
                    const subStatus = sub.optIn?.status || sub.status || 'pending';

                    return (
                      <tr
                        key={sub.id}
                        className={`hover:bg-slate-50/70 transition-colors ${
                          isSelected ? 'bg-indigo-50/30' : ''
                        }`}
                      >
                        {/* Checkbox */}
                        <td className="py-3 px-4">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleRow(sub.id)}
                            className="rounded border-slate-300 text-indigo-600 focus:ring-0"
                          />
                        </td>

                        {/* Contact info (Click to open drawer) */}
                        <td
                          className="py-3 px-4 cursor-pointer"
                          onClick={() => setActiveSubscriber(sub)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-700 font-bold flex items-center justify-center text-xs shrink-0">
                              {(sub.name || sub.phoneE164 || 'S').slice(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-semibold text-slate-900 hover:text-indigo-600 transition-colors">
                                {sub.name || t('subs.unnamed') || 'Unnamed'}
                              </div>
                              <div className="text-[11px] font-mono text-slate-500">
                                {sub.phoneE164}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Status Badge */}
                        <td className="py-3 px-4">
                          {subStatus === 'subscribed' ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              {t('subs.status.subscribed') || 'Subscribed'}
                            </span>
                          ) : subStatus === 'unsubscribed' ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                              {t('subs.status.unsubscribed') || 'Opted Out'}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                              {t('subs.status.pending') || 'Pending'}
                            </span>
                          )}
                        </td>

                        {/* Labels */}
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {sub.labelIds && sub.labelIds.length > 0 ? (
                              sub.labelIds.map((lid) => {
                                const l = labelMap.get(lid);
                                return (
                                  <span
                                    key={lid}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-white border border-line text-ink shadow-2xs"
                                    style={l?.color ? { borderColor: l.color } : undefined}
                                  >
                                    <span
                                      className="w-2 h-2 rounded-full bg-ink-4"
                                      style={l?.color ? { backgroundColor: l.color } : undefined}
                                    />
                                    <span>{l?.name || lid}</span>
                                  </span>
                                );
                              })
                            ) : (
                              <span className="text-slate-300 text-[11px]">—</span>
                            )}
                          </div>
                        </td>

                        {/* Lists */}
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {sub.listIds && sub.listIds.length > 0 ? (
                              sub.listIds.map((lid) => {
                                const lst = listMap.get(lid);
                                return (
                                  <span
                                    key={lid}
                                    className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-medium"
                                  >
                                    {lst?.name || lid}
                                  </span>
                                );
                              })
                            ) : (
                              <span className="text-slate-300 text-[11px]">—</span>
                            )}
                          </div>
                        </td>

                        {/* Country */}
                        <td className="py-3 px-4 text-slate-600">
                          {sub.country ? (
                            <span className="font-semibold text-slate-800 uppercase">
                              {sub.country}
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>

                        {/* Last Seen */}
                        <td className="py-3 px-4 text-slate-500 text-[11px]">
                          {sub.lastSeenAt
                            ? new Date(sub.lastSeenAt).toLocaleDateString()
                            : sub.createdAt
                            ? new Date(sub.createdAt).toLocaleDateString()
                            : '—'}
                        </td>

                        {/* Row Actions */}
                        <td className="py-3 px-4 text-end">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setActiveSubscriber(sub)}
                              className="text-[11px] py-1 px-2"
                            >
                              {t('common.details') || 'Details'}
                            </Button>
                            <button
                              type="button"
                              onClick={() => handleDeleteSubscriber(sub.id)}
                              className="p-1 text-slate-400 hover:text-rose-600 transition-colors"
                              title={t('subs.deleteContact') || 'Delete contact'}
                            >
                              <TrashIcon size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Card Layout (< 768px) */}
          <div className="md:hidden space-y-3">
            {pagedSubscribers.map((sub) => {
              const isSelected = selectedIds.has(sub.id) || selectAllMatching;
              const subStatus = sub.optIn?.status || sub.status || 'pending';

              return (
                <div
                  key={sub.id}
                  className={`p-4 bg-white rounded-xl border transition-all ${
                    isSelected
                      ? 'border-indigo-300 bg-indigo-50/20'
                      : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleRow(sub.id)}
                        className="rounded border-slate-300 text-indigo-600 focus:ring-0 mt-0.5"
                      />
                      <div
                        onClick={() => setActiveSubscriber(sub)}
                        className="cursor-pointer"
                      >
                        <h4 className="text-xs font-bold text-slate-900">
                          {sub.name || t('subs.unnamed') || 'Unnamed'}
                        </h4>
                        <span className="text-[11px] font-mono text-slate-500">
                          {sub.phoneE164}
                        </span>
                      </div>
                    </div>

                    {subStatus === 'subscribed' ? (
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {t('subs.status.subscribed') || 'Subscribed'}
                      </span>
                    ) : subStatus === 'unsubscribed' ? (
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                        {t('subs.status.unsubscribed') || 'Opted Out'}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                        {t('subs.status.pending') || 'Pending'}
                      </span>
                    )}
                  </div>

                  {/* Labels on mobile */}
                  {sub.labelIds && sub.labelIds.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {sub.labelIds.map((lid) => {
                        const l = labelMap.get(lid);
                        return (
                          <span
                            key={lid}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-white border border-line text-ink"
                            style={l?.color ? { borderColor: l.color } : undefined}
                          >
                            <span
                              className="w-2 h-2 rounded-full bg-ink-4"
                              style={l?.color ? { backgroundColor: l.color } : undefined}
                            />
                            <span>{l?.name || lid}</span>
                          </span>
                        );
                      })}
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                    <span className="text-slate-400 text-[11px]">
                      {sub.country ? `${t('subs.col.country') || 'Country'}: ${sub.country.toUpperCase()}` : ''}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setActiveSubscriber(sub)}
                    >
                      {t('subs.viewDetails') || 'View Details'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 6. Pagination Footer */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-white rounded-xl border border-slate-200 text-xs">
            <div className="flex items-center gap-2 text-slate-500">
              <span>{t('subs.rowsPerPage') || 'Rows per page:'}</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="rounded-lg border border-slate-200 bg-white px-2 py-1 outline-none focus:border-indigo-500"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="ms-2">
                {t('subs.showingRange', { from: (safeCurrentPage - 1) * pageSize + 1, to: Math.min(safeCurrentPage * pageSize, totalItems), total: totalItems }) || `Showing ${(safeCurrentPage - 1) * pageSize + 1}–${Math.min(safeCurrentPage * pageSize, totalItems)} of ${totalItems} contacts`}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={safeCurrentPage <= 1}
                className="p-1.5"
              >
                <ChevronLeftIcon size={16} className="rtl:rotate-180" />
              </Button>
              <span className="px-2 font-medium text-slate-700">
                {t('subs.pageOf', { page: safeCurrentPage, total: totalPages }) || `Page ${safeCurrentPage} of ${totalPages}`}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={safeCurrentPage >= totalPages}
                className="p-1.5"
              >
                <ChevronRightIcon size={16} className="rtl:rotate-180" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Drawers & Modals ──────────────────────────────────────────────── */}

      {/* Subscriber Detail Drawer */}
      <SubscriberDrawer
        open={Boolean(activeSubscriber)}
        onClose={() => setActiveSubscriber(null)}
        subscriber={activeSubscriber}
        onUpdated={() => {
          if (activeSubscriber) {
            const updated = subscribers.find((s) => s.id === activeSubscriber.id);
            if (updated) setActiveSubscriber(updated);
          }
        }}
        onOpenLabelsManager={() => setIsLabelsModalOpen(true)}
        onOpenListsManager={() => setIsListsModalOpen(true)}
        onOpenCustomFieldsManager={() => setIsCustomFieldsModalOpen(true)}
      />

      {/* Add Single Subscriber Modal */}
      <AddSubscriberModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => showNotification('success', t('subs.addSuccess') || 'Subscriber added successfully.')}
      />

      {/* Import Subscribers Modal */}
      <ImportSubscribersModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={() => showNotification('success', t('subs.importSuccess') || 'Contacts imported successfully.')}
      />

      {/* Labels Manager Modal */}
      <LabelsManagerModal
        isOpen={isLabelsModalOpen}
        onClose={() => setIsLabelsModalOpen(false)}
        labels={labels}
      />

      {/* Lists Manager Modal */}
      <ListsManagerModal
        isOpen={isListsModalOpen}
        onClose={() => setIsListsModalOpen(false)}
        lists={lists}
      />

      {/* Custom Fields Manager Modal */}
      <CustomFieldsManagerModal
        isOpen={isCustomFieldsModalOpen}
        onClose={() => setIsCustomFieldsModalOpen(false)}
        customFields={customFields}
      />

      {/* Bulk Resubscribe Compliance Consent Modal */}
      <ConsentNoteDialog
        isOpen={isConsentModalOpen}
        onClose={() => setIsConsentModalOpen(false)}
        onConfirm={handleBulkConsentConfirm}
        targetCount={effectiveSelectedCount}
        mode={consentMode}
      />
    </div>
  );
}
